import React, { useState} from "react";
import { Box, IconButton, Tooltip, Typography, styled } from "@mui/material";
import {
  FileText,
  Youtube,
  RotateCw,
  Trash2,
  ArrowRight,
  Book,
  Upload,
  Share2,
  ListVideo,
  Copy
} from "lucide-react";

// Types
import { SeriesType, RunData, ShareRunMapData } from "../types/MeGenTypes";
import { isStoppedByUser } from "../MeGenRunStatus";

type EpisodeItemProps = {
  episode: RunData;
  seriesName?: string;
  onSelect?: (episodeId: string, runType?: SeriesType) => void;
  onRestart?: (episodeId: string) => void;
  onDuplicate?: (episodeId: string) => void;
  onDelete?: (episodeId: string) => void;
  onShare?: (shareData: ShareRunMapData) => void;
};

// ============ Styled Components ============

const ItemContainer = styled(Box)<{ isHovered: boolean }>(({ isHovered }) => ({
  display: "flex",
  alignItems: "center",
  padding: "0.75rem 1rem",
  borderRadius: "0.75rem",
  border: `1px solid ${isHovered ? "#fce4ec" : "#f1f5f9"}`,
  backgroundColor: isHovered ? "#fffbfc" : "white",
  transition: "all 0.2s ease",
  boxShadow: isHovered
    ? "0 4px 6px -1px rgba(233, 30, 99, 0.08)"
    : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  cursor: "pointer",
  marginBottom: "0.75rem",
  "&:last-child": {
    marginBottom: 0,
  },
}));

const IconWrapper = styled(Box)<{ hasVideo: boolean; isHovered: boolean }>(
  ({ hasVideo, isHovered }) => ({
    width: "2.5rem",
    height: "2.5rem",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background-color 0.2s",
    backgroundColor: hasVideo
      ? isHovered
        ? "#fee2e2"
        : "#fef2f2"
      : isHovered
        ? "#f8d8e1"
        : "#fce4ec",
    color: hasVideo ? "#dc2626" : "#e91e63",
  })
);

const InfoSection = styled(Box)({
  flex: 1,
  minWidth: 0,
  marginLeft: "0.75rem",
});

const Title = styled(Typography)({
  fontWeight: 600,
  fontSize: "0.875rem",
  color: "#1e293b",
  transition: "color 0.2s",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const SeriesLabel = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: "0.25rem",
  padding: "1px 8px",
  borderRadius: "9999px",
  fontSize: "0.65rem",
  fontWeight: 500,
  backgroundColor: "#f0f9ff",
  color: "#0369a1",
  border: "1px solid #bae6fd",
  whiteSpace: "nowrap",
  flexShrink: 0,
  maxWidth: "140px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  cursor: "default",
});

const MetaRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  marginTop: "0.25rem",
  fontSize: "0.75rem",
  color: "#64748b",
  flexWrap: "wrap",
});

const IdBadge = styled("span")({
  fontFamily: "monospace",
  backgroundColor: "#f1f5f9",
  padding: "0.125rem 0.375rem",
  borderRadius: "0.25rem",
  fontSize: "0.625rem",
});

const StatusBadge = styled("span")<{ status: string }>(({ status }) => ({
  padding: "0.125rem 0.5rem",
  borderRadius: "9999px",
  fontSize: "0.625rem",
  textTransform: "uppercase",
  fontWeight: "bold",
  backgroundColor:
    status === "completed"
      ? "#dcfce7"
      : status === "error"
        ? "#fee2e2"
        : status === "stopped"
          ? "#fef3c7"
          : "#dbeafe",
  color:
    status === "completed"
      ? "#15803d"
      : status === "error"
        ? "#dc2626"
        : status === "stopped"
          ? "#b45309"
          : "#1d4ed8",
}));

const VideoIdBadge = styled("span")({
  display: "flex",
  alignItems: "center",
  gap: "0.25rem",
  color: "#ef4444",
  fontWeight: 500,
  fontSize: "0.625rem",
});

const MetaColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  textAlign: "right",
  marginRight: "0.75rem",
  fontSize: "0.75rem",
  color: "#64748b",
  minWidth: "100px",
});

const CreatedByRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.375rem",
  marginBottom: "0.25rem",
});

const Avatar = styled("img")({
  width: "1rem",
  height: "1rem",
  borderRadius: "50%",
});

const AvatarPlaceholder = styled(Box)({
  width: "1rem",
  height: "1rem",
  borderRadius: "50%",
  backgroundColor: "#e2e8f0",
});

const ActionsSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.25rem",
});

const ActionButton = styled(IconButton)<{ variant?: "default" | "danger" }>(
  ({ variant = "default" }) => ({
    padding: "0.375rem",
    color: "#94a3b8",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: variant === "danger" ? "#fef2f2" : "#eff6ff",
      color: variant === "danger" ? "#dc2626" : "#2563eb",
    },
  })
);

const ArrowButton = styled(IconButton)({
  padding: "0.375rem",
  color: "#cbd5e1",
  transition: "all 0.2s",
  "&:hover": {
    color: "#f382a0",
    transform: "translateX(4px)",
  },
});

// ============ Component ============

const EpisodeItem: React.FC<EpisodeItemProps> = (props) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    props.onSelect?.(props.episode.id, props.episode.run_type);
  };

  const handleRestart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    props.onRestart?.(props.episode.id);
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    props.onDuplicate?.(props.episode.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    props.onDelete?.(props.episode.id);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const data: ShareRunMapData = {
      id: props.episode.id,
      project_id: props.episode.project_id,
      series_id: props.episode.series_id,
      name: props.episode.title || `Episode ${props.episode.id.slice(0, 8)}`,
    };

    props.onShare?.(data);
  };

  const hasVideo = !!props.episode.video_id;

  const formattedDate = props.episode.created_at
    ? new Date(props.episode.created_at).toLocaleString("th-TH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Unknown Date";

  const getDeviceIcon = () => {
    const deviceName = props.episode.device_name || "";
    if (deviceName.includes("Mobile")) return "📱";
    if (deviceName.includes("Mac")) return "💻 Mac";
    if (deviceName.includes("Win")) return "💻 Windows";
    if (deviceName.includes("Linux")) return "🐧 Linux";
    return "💻";
  };

  const getRunIcon = () => {
    const runType = props.episode.run_type || "";

    switch (runType) {
      case "rss_news":
        if (hasVideo) {
          return <Youtube size={18} />;
        } else {
          return <Upload size={18} />;
        }
      
      case "script2vdo":
        return <Book size={18} />;
      
      case "story2scene":
        return <FileText size={18} />;
      
      default:
        return <Upload size={18} />;
    }
  };

  // Badge
  // GET /runs ตั้ง status="error" ทุกครั้งที่ error ไม่ว่าง (run_index_service.py:419) — ต้องดูข้อความเองว่าเป็นการหยุดโดยผู้ใช้
  const isStopped = isStoppedByUser(props.episode.error);
  const displayStatus = isStopped ? "stopped" : props.episode.status;
  const displayLabel = isStopped ? "Stopped" : props.episode.status;

  return (
    <ItemContainer
      id={`megen-episode-item-${props.episode.id}-container`}
      isHovered={isHovered}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {/* Icon */}
      <IconWrapper id={`megen-episode-item-${props.episode.id}-icon`} hasVideo={hasVideo} isHovered={isHovered}>
        {getRunIcon()}
      </IconWrapper>

      {/* Info */}
      <InfoSection id={`megen-episode-item-${props.episode.id}-info`}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <Title id={`megen-episode-item-${props.episode.id}-title`}>
            {props.episode.title || `Episode ${props.episode.id.slice(0, 8)}`}
          </Title>
          {props.seriesName && (
            <SeriesLabel onClick={(e) => e.stopPropagation()}>
              <ListVideo size={10} />
              {props.seriesName}
            </SeriesLabel>
          )}
        </Box>
        <MetaRow id={`megen-episode-item-${props.episode.id}-meta`}>
          <IdBadge>{props.episode.id.slice(0, 8)}...</IdBadge>
          <span>{formattedDate}</span>
          {props.episode.video_id && (
            <VideoIdBadge>
              <Youtube size={10} /> {props.episode.video_id}
            </VideoIdBadge>
          )}
          <StatusBadge status={displayStatus}>{displayLabel}</StatusBadge>
        </MetaRow>
      </InfoSection>

      {/* Metadata Column */}
      <MetaColumn>
        {props.episode.created_by && (
          <CreatedByRow title={props.episode.created_by}>
            <span style={{ color: "#94a3b8" }}>by</span>
            <span
              style={{
                fontWeight: 500,
                color: "#334155",
                maxWidth: "80px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {props.episode.created_by}
            </span>
            {props.episode.created_by_avatar ? (
              <Avatar src={props.episode.created_by_avatar} alt="User" />
            ) : (
              <AvatarPlaceholder />
            )}
          </CreatedByRow>
        )}
        {(props.episode.hostname || props.episode.device_name) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              opacity: 0.7,
              fontSize: "0.625rem",
            }}
            title={props.episode.hostname}
          >
            <span>
              {getDeviceIcon()} {props.episode.hostname}
            </span>
          </Box>
        )}
      </MetaColumn>

      {/* Actions */}
      <ActionsSection id={`megen-episode-item-${props.episode.id}-actions`}>
        <Tooltip title="Share" placement="top">
          <ActionButton
            id={`megen-episode-item-${props.episode.id}-share-btn`}
            size="small"
            onClick={handleShare}
          >
            <Share2 size={16} />
          </ActionButton>
        </Tooltip>
        {/*
          // todo --- ตอนนี้ rerun ใช้งานได้เฉพาะ rss_news
        */}
        {props.episode.run_type === "rss_news" && (
          <Tooltip title="Restart Run (Overwrite)" placement="top">
            <ActionButton
              id={`megen-episode-item-${props.episode.id}-restart-btn`}
              size="small"
              onClick={handleRestart}
            >
              <RotateCw size={16} />
            </ActionButton>
          </Tooltip>
        )}
        {props.episode.run_type === "script2vdo" && (
          <Tooltip title="Duplicate Run" placement="top">
            <ActionButton
              id={`megen-episode-item-${props.episode.id}-duplicate-btn`}
              size="small"
              onClick={handleDuplicate}
            >
              <Copy size={16} />
            </ActionButton>
          </Tooltip>
        )}
        <Tooltip title="Delete Run" placement="top">
          <ActionButton
            id={`megen-episode-item-${props.episode.id}-delete-btn`}
            size="small"
            variant="danger"
            onClick={handleDelete}
          >
            <Trash2 size={16} />
          </ActionButton>
        </Tooltip>
        <Tooltip title="View Details" placement="top">
          <ArrowButton id={`megen-episode-item-${props.episode.id}-view-btn`} size="small">
            <ArrowRight size={14} />
          </ArrowButton>
        </Tooltip>
      </ActionsSection>
    </ItemContainer>
  );
};

export default EpisodeItem;